/**
 * About Advanced - Premium Sections JS
 * Pat Nutrition
 * 
 * Features:
 * - Parallax scrolling (RAF-based)
 * - Reveal animations (IntersectionObserver)
 * - Testimonials slider
 * - Respects prefers-reduced-motion
 * - Shopify Theme Editor compatible
 */

(function() {
  'use strict';

  // Guard against double initialization
  if (window.__aboutAdvancedInit) return;
  window.__aboutAdvancedInit = true;

  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Initialize all features
   */
  function init() {
    initRevealAnimations();
    if (!prefersReducedMotion) {
      initParallax();
    }
    initTestimonialsSlider();
  }

  /**
   * Cleanup for Shopify editor
   */
  function cleanup() {
    // Remove any active observers/listeners if needed
    if (window.__aboutParallaxRAF) {
      cancelAnimationFrame(window.__aboutParallaxRAF);
      window.__aboutParallaxRAF = null;
    }
  }

  /* ==============================================
     REVEAL ANIMATIONS
     ============================================== */
  function initRevealAnimations() {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals.length) return;

    // Use IntersectionObserver for performance
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            // Optionally unobserve after reveal
            observer.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.1
      }
    );

    reveals.forEach((el) => observer.observe(el));
  }

  /* ==============================================
     PARALLAX SCROLLING
     ============================================== */
  function initParallax() {
    const parallaxElements = document.querySelectorAll('[data-parallax]');
    if (!parallaxElements.length) return;

    let ticking = false;
    let scrollY = window.scrollY;

    function updateParallax() {
      parallaxElements.forEach((el) => {
        const factor = parseFloat(el.dataset.parallax) || 0.2;
        const rect = el.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        
        // Only animate if element is in viewport
        if (rect.bottom > 0 && rect.top < viewportHeight) {
          // Calculate parallax offset based on element position
          const elementCenter = rect.top + rect.height / 2;
          const viewportCenter = viewportHeight / 2;
          const offset = (elementCenter - viewportCenter) * factor;
          
          el.style.transform = `translateY(${offset}px)`;
        }
      });
      
      ticking = false;
    }

    function onScroll() {
      scrollY = window.scrollY;
      
      if (!ticking) {
        window.__aboutParallaxRAF = requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }

    // Initial update
    updateParallax();

    // Scroll listener with passive option for performance
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateParallax, { passive: true });
  }

  /* ==============================================
     TESTIMONIALS SLIDER
     ============================================== */
  function initTestimonialsSlider() {
    const sliders = document.querySelectorAll('[data-testimonials-slider]');
    
    sliders.forEach((slider) => {
      const track = slider.querySelector('[data-testimonials-track]');
      const prevBtn = slider.querySelector('[data-testimonials-prev]');
      const nextBtn = slider.querySelector('[data-testimonials-next]');
      
      if (!track) return;
      
      const slides = track.querySelectorAll('.about-testimonial');
      if (!slides.length) return;

      let currentIndex = 0;
      let slidesPerView = getSlidesPerView();
      let maxIndex = Math.max(0, slides.length - slidesPerView);
      
      // Touch/swipe support
      let startX = 0;
      let currentX = 0;
      let isDragging = false;

      function getSlidesPerView() {
        if (window.innerWidth >= 990) return 3;
        if (window.innerWidth >= 768) return 2;
        return 1;
      }

      function updateSlider() {
        const slideWidth = 100 / slidesPerView;
        const offset = -currentIndex * slideWidth;
        track.style.transform = `translateX(${offset}%)`;
        
        // Update button states
        if (prevBtn) prevBtn.disabled = currentIndex === 0;
        if (nextBtn) nextBtn.disabled = currentIndex >= maxIndex;
      }

      function goToSlide(index) {
        currentIndex = Math.max(0, Math.min(index, maxIndex));
        updateSlider();
      }

      function nextSlide() {
        goToSlide(currentIndex + 1);
      }

      function prevSlide() {
        goToSlide(currentIndex - 1);
      }

      // Button handlers
      if (prevBtn) prevBtn.addEventListener('click', prevSlide);
      if (nextBtn) nextBtn.addEventListener('click', nextSlide);

      // Touch/swipe handlers
      track.addEventListener('pointerdown', (e) => {
        if (prefersReducedMotion) return;
        isDragging = true;
        startX = e.clientX;
        track.style.cursor = 'grabbing';
      });

      track.addEventListener('pointermove', (e) => {
        if (!isDragging) return;
        currentX = e.clientX;
      });

      track.addEventListener('pointerup', () => {
        if (!isDragging) return;
        isDragging = false;
        track.style.cursor = '';
        
        const diff = startX - currentX;
        const threshold = 50;
        
        if (diff > threshold) {
          nextSlide();
        } else if (diff < -threshold) {
          prevSlide();
        }
      });

      track.addEventListener('pointerleave', () => {
        isDragging = false;
        track.style.cursor = '';
      });

      // Handle resize
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          slidesPerView = getSlidesPerView();
          maxIndex = Math.max(0, slides.length - slidesPerView);
          if (currentIndex > maxIndex) currentIndex = maxIndex;
          updateSlider();
        }, 100);
      });

      // Initial update
      updateSlider();

      // Auto-slide (optional, disabled for reduced motion)
      if (!prefersReducedMotion && slider.dataset.autoSlide) {
        const autoInterval = parseInt(slider.dataset.autoSlide) || 5000;
        setInterval(() => {
          if (currentIndex >= maxIndex) {
            currentIndex = 0;
          } else {
            currentIndex++;
          }
          updateSlider();
        }, autoInterval);
      }
    });
  }

  /* ==============================================
     MARQUEE PAUSE ON HOVER
     ============================================== */
  function initMarquee() {
    const marquees = document.querySelectorAll('.about-marquee__track');
    
    marquees.forEach((marquee) => {
      marquee.addEventListener('mouseenter', () => {
        marquee.style.animationPlayState = 'paused';
      });
      
      marquee.addEventListener('mouseleave', () => {
        marquee.style.animationPlayState = 'running';
      });
    });
  }

  /* ==============================================
     EVENT LISTENERS
     ============================================== */
  
  // Init on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify Theme Editor support
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('[data-about-section]')) {
      // Re-init reveal animations for new section
      initRevealAnimations();
      initTestimonialsSlider();
      initMarquee();
    }
  });

  document.addEventListener('shopify:section:unload', cleanup);

})();

